/**
 * FHIR coding-system detection. A `Coding.system` value varies widely across
 * EHRs (e.g. "http://hl7.org/fhir/sid/icd-10", "ICD-10-CM", "icd10cm"); detect
 * by case-insensitive substring against a short lowercase hint. Intentionally
 * loose — `'icd'` spans ICD-10-CM/PCS/9 — and distinct from the canonical
 * system registry in `@agilemd/terminology-client` (`CODE_SYSTEM_URLS`).
 */
export const CODING_SYSTEM_HINTS = {
  ICD10: 'icd',
  SNOMED: 'snomed',
  RXNORM: 'rxnorm',
  CPT: 'cpt',
  LOINC: 'loinc'
} as const;

export function systemIncludes(system: string | undefined, hint: string): boolean {
  return (system ?? '').toLowerCase().includes(hint);
}

/**
 * FHIR Condition.category codes (STU3/R4). `problem-list-item` is the
 * patient-level longitudinal problem list; `encounter-diagnosis` is a visit
 * diagnosis tied to one encounter via Condition.context. Shared by the org
 * FHIR fetch URLs, the population-criteria extractors, and experiment replay.
 */
export const CONDITION_CATEGORIES = {
  PROBLEM_LIST_ITEM: 'problem-list-item',
  ENCOUNTER_DIAGNOSIS: 'encounter-diagnosis'
} as const;

export type ConditionCategory = (typeof CONDITION_CATEGORIES)[keyof typeof CONDITION_CATEGORIES];

/**
 * SNOMED-CT category axes for the FHIR `Procedure.category` search. `surgical`
 * + `diagnostic` together span the procedure vocabulary the criteria use. Epic
 * honors the category filter; Vault STU3 does not (see `proceduresUrl`).
 */
export const PROCEDURE_CATEGORIES = {
  SURGICAL: '387713003',
  DIAGNOSTIC: '103693007'
} as const;

export type ProcedureCategory = (typeof PROCEDURE_CATEGORIES)[keyof typeof PROCEDURE_CATEGORIES];
